/*!
 * modal.js – Modal Utility
 * Version: 3.0.0
 * Author: Homero Cavazos
 *
 * Description:
 * A lightweight JavaScript helper for managing modals. 
 * Supports triggers, gated content, demos, copy-to-clipboard, and
 * auto-closing behavior.
 *
 * Usage:
 *   new modal({debug:true});
 * 
 *   const m = new modal({ debug: true });
 *   m.init();
 */

"use strict";

class modal {
  constructor(opts = {}) {
    // Default settings
    this.settings = Object.assign(
      {
        debug: false,
        modalID: "EmbedModal",
        modalTrigger: "js-modal-trigger",
        iframeTitle: "Aya Healthcare",
        videoContainer: ".modal-video-container",
      },
      opts
    );

    // Auto-init
    this.init();
  }

  setOpts(opts = {}) {
    Object.assign(this.settings, opts);
  }

  init() {
    document.addEventListener("DOMContentLoaded", () => {
      if (this.settings.debug) {
        console.log("Modal settings:", this.settings);
      }
      this.onLoad();
    });
  }

  onLoad() {

    // Listen to clicks for triggers (works even if items are added later)
    document.body.addEventListener("click", event => {
      const trigger = this.getTriggerElement(event);
      if (trigger) {
        event.preventDefault();
        this.open(trigger);
      }
    });

    // Close modal event listeners
    document
      .querySelectorAll(
        ".modal-background, .modal-close, .modal-card-head .delete, .modal-card-foot .button"
      )
      .forEach($close => {
        const $target = $close.closest(".modal:not(.gated)");
        $close.addEventListener("click", () => this.close($target));
      });
  }

  getTriggerElement(event) {
    const { modalTrigger } = this.settings;

    if (event.target.parentNode?.classList.contains(modalTrigger)) {
      return event.target.parentNode;
    }

    if (
      event.target.nodeName === "IMG" &&
      event.target.parentNode?.parentNode?.classList.contains(modalTrigger)
    ) {
      return event.target.parentNode.parentNode;
    }

    if (event.target.classList.contains(modalTrigger)) {
      return event.target;
    }

    return null;
  }

  open(trigger) {
    const modal_id = trigger.dataset.modal || this.settings.modalID;

    const is_iframe = trigger.dataset.src ? true : false;
    // This needs to "let" so its not read-only
    let src = trigger.dataset.src ? trigger.dataset.src : trigger.href;
    
    const modalEl = document.getElementById(modal_id);    

    if (!modalEl) {
      console.warn(`modal: No element found with id "${modal_id}"`);
      return;
    }

    modalEl.classList.add("is-active");

    if (src) {
      // Handle gated content
      if (trigger.hasAttribute("data-gated-content")) {
        src = atob(src)
          .replace(/&amp;/g, "&")
          .replace(/&lt;/g, "<")
          .replace(/&gt;/g, ">")
          .replace(/&quot;/g, '"')
          .replace(/&#39;/g, "'");
      }

      // Apply demo styling if needed
      if (trigger.dataset.resourcetype === "demo") {
        modalEl
          .querySelector(this.settings.videoContainer)
          .classList.add("modal-demo-container");
      } else {
        modalEl
          .querySelector(this.settings.videoContainer)
          .classList.remove("modal-demo-container");
      }

    // This is a video link to create an iframe
    if ( !is_iframe ) {

      const iframe = document.createElement('iframe');
      iframe.src = src; // Set the source URL
      iframe.frameBorder = '0'; // Remove border (optional)
      iframe.allowFullscreen = true; // Enable fullscreen (optional)
      iframe.title = this.settings.iframeTitle; // Set title for accessibility

      modalEl.querySelector(this.settings.videoContainer).appendChild(iframe);

    } else { 
      // This is a full iframe embed
      modalEl.querySelector(this.settings.videoContainer).innerHTML = src;
    }

    }
  }

  close(el) {
    if (!el) return;

    el.classList.remove("is-active");
    el.querySelector(this.settings.videoContainer).innerHTML = "";

    const event = new CustomEvent("modalClosed", { detail: { modal: el } });
    document.dispatchEvent(event);

  }

  closeAll() {
    document.querySelectorAll(".modal:not(.gated)").forEach(modalEl => {
      this.close(modalEl);
    });
  }

  simulateClick(trigger) {
    const event = new MouseEvent("click", {
      bubbles: true,
      cancelable: true,
    });
    trigger.dispatchEvent(event);
    this.open(trigger);
  }


}

export { modal };
